import React from 'react';
import styled from 'styled-components/macro';
import { theme } from 'lib/style';
import { reportData } from 'lib/const';
import {
  deleteResellerReport,
  fetchCustomersByResellerId,
  getResellerReports,
} from 'lib/api';
import {
  loadCustomReports,
  ReportDataProps,
  ResellerReport,
  useAuth,
  useCustomReportsContext,
  useGlobalState,
} from 'lib/context';
import { Field, ModalCustomTableFields } from 'lib/components/modal';
import { LoadingIndicator } from 'lib/components';
import {
  CustomReportModal,
  CustomReportItem,
  UsersSelectorModal,
  timeRangeConstants,
} from '../../components';
import { CustomersSelectorModal } from '../../components/CustomersSelectorModal';

/*** SUS-781 changes ***/
import Pagination from 'app/pages/design/common/Pagination';
import _ from 'lodash';
import RecurringReportItem from '../../components/RecurringReportItem';
import CalendarIcon from 'lib/images/CalendarIcon';
import CalendarMonthlyIcon from 'lib/images/CalendarMonthly';
import SelectCustomersModal from '../../components/SelectCustomersModal';
import SimplifiedRecurringModal from '../../components/SimplifiedRecurringModal';
import { successToast } from 'lib/components/toasts/success';
import DeactivateReportModal from '../../components/DeactivateReportModal';
import { Heading } from 'lib/components/styles/typography';
import { Gap, HeaderWrapper } from 'lib/components/styles/layout';
import { VIDEO_ATTRIBUTES } from 'lib/utils/videoAttributes';
import { useToastError } from 'lib/hooks';
import { Container, MainWrapper } from 'lib/components/styles/layout';
import { Button } from 'react-covideo-common';
import { FiPlus } from 'react-icons/fi';

interface ReportsParams {
  setTotalItems: React.Dispatch<React.SetStateAction<number>>;
  setLastPage: React.Dispatch<React.SetStateAction<number>>;
  count: number;
  limit: number;
}
/******/

const NoReports = styled.div`
  min-height: 400px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  font-size: 18px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.33;
  letter-spacing: normal;
  text-align: center;
  color: #272a32;
  & > div {
    margin-bottom: 32px;
  }
`;

const RecurringReportContainer = styled.div`
  display: flex;
  justify-content: space-between;
  background-color: ${theme.palette.covideoSecondaryButtonBg};
  border-radius: 12px;
  padding: 16px;
  gap: 16px;
`;

const sectionName = 'Custom Reports';

type CustomerOptions = {
  id: number;
  checked: boolean;
  business: string;
};

/*** SUS-781 changes ***/
const customReportsParams = _.debounce(async (params: ReportsParams) => {
  const { limit, setLastPage, setTotalItems, count } = params;
  setTotalItems(count);
  setLastPage(Math.ceil(count / limit));
}, 500);
/******/
export const List = () => {
  /*** SUS-781 changes ***/
  const [limit, setLimit] = React.useState(10);
  const [page, setPage] = React.useState(0);
  const [totalItems, setTotalItems] = React.useState(0);
  const [lastPage, setLastPage] = React.useState(1);
  const [deleteReport, setDeleteReport] = React.useState(false);
  /******/
  const [showCustomizeReportModal, setShowCustomizeReportModal] =
    React.useState(false);
  const [showNewReportModal, setShowNewReportModal] = React.useState(false);
  const { userData } = useAuth();

  const allReports: Field[] = Object.keys(reportData)
    .filter(
      reportValue =>
        !reportData[reportValue].isAutomotive ||
        (!!userData.isAutomotive && !!reportData[reportValue].isAutomotive)
    )
    .map(reportValue => {
      const isVideoAttributes = reportValue.includes(VIDEO_ATTRIBUTES);
      if (isVideoAttributes)
        return {
          value: 'video-attributes~~details',
          label: 'Video Attributes',
        };

      return {
        value: reportValue,
        label: reportData[reportValue].label,
      };
    });

  const [initialReports, setInitialReports] = React.useState<Field[]>(
    allReports.slice(0, 3)
  );
  const [showCustomerSelectorModal, setShowCustomerSelectorModal] =
    React.useState(false);
  const [showUserSelectorModal, setShowUserSelectorModal] =
    React.useState(false);
  const [selectedUsers, setSelectedUsers] = React.useState([] as string[]);
  const [isSelectCustomerModalOpen, setIsSelectCustomerModalOpen] =
    React.useState(false);
  const [isCustomReportModalOpen, setIsCustomReportModalOpen] =
    React.useState(false);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] =
    React.useState(false);

  const [allCustomers, setAllCustomers] = React.useState<CustomerOptions[]>([]);
  const [recurringReportData, setRecurringReportData] =
    React.useState<ReportDataProps>({
      type: '',
    });

  const [resellerReports, setResellerReports] = React.useState<
    ResellerReport[]
  >([]);
  const [customerList, setCustomerList] = React.useState<number[]>([]);
  const [selectedReport, setSelectedReport] = React.useState({
    type: '',
    reportId: 0,
  });
  const { showError } = useToastError();

  const formCustomerList = (selectedCustomers: Array<any>) => {
    // code to form list of emails for customer
    const result = selectedCustomers
      .filter(item => item.checked)
      .map(item => item.id);
    setCustomerList(result);
  };

  const formCustomerListFromReport = (report: any) => {
    const tmp = [...allCustomers];
    tmp.forEach((item: any) => {
      const containedInReport = report.customers.find(
        (cust: any) => cust === item.id
      );
      if (containedInReport) item.checked = true;
    });
    setAllCustomers(tmp);
  };

  const resetStates = async () => {
    const temp = [...allCustomers];
    temp.forEach((item, index) => {
      temp[index].checked = false;
    });
    setAllCustomers(temp);
    setCustomerList([]);
    setSelectedReport({ type: '', reportId: 0 });
    setRecurringReportData({ type: '' });
    await fetchResellerReportData();
  };

  const deactivateReport = async (reportId: number) => {
    try {
      if (!reportId) return;
      await deleteResellerReport(reportId);
      resetStates();
      successToast({ title: 'Successfully deactivated report.' });
    } catch (e) {
      showError(
        'custom-error',
        'There was an error deactivating your report. Please try again.'
      );
    }
  };

  const fetchCustomers = async () => {
    const result = await fetchCustomersByResellerId(
      Number(userData.customerId),
      100,
      0,
      true
    );
    if (result.length) {
      const formattedResult = result.map((item: any) => ({
        id: item.customerId,
        business: item.business,
        checked: false,
      }));
      setAllCustomers(formattedResult);
    }
  };
  const checkIfReportIsActive = (range: string) => {
    return resellerReports.find((item: any) => item.range === range);
  };
  const fetchResellerReportData = async () => {
    try {
      const result = await getResellerReports();
      setResellerReports(result);
    } catch (e) {
      console.log(e);
    }
  };

  const {
    state: { whitelabel },
  } = useGlobalState();

  const {
    userData: { isResellerAdmin, isCompanyAdmin },
  } = useAuth();

  const {
    state: { customReports, loading, count } /*** SUS-781 changes ***/,
    dispatch: customReportsDispatch,
  } = useCustomReportsContext();

  /*** SUS-781 changes ***/
  React.useEffect(() => {
    customReportsParams({ setTotalItems, setLastPage, count, limit });
  });

  React.useEffect(() => {
    const resellerReportingEnabled =
      userData.reseller?.resellerReportingEnabled &&
      userData.reseller.resellerReportingEnabled.toString() === '1';
    if (!resellerReportingEnabled) {
      return;
    }
    fetchCustomers();
    fetchResellerReportData();
  }, [userData]);

  React.useEffect(() => {
    loadCustomReports({
      start: page > 0 ? page * limit : 0,
      limit,
      dispatch: customReportsDispatch,
    });
  }, [customReportsDispatch, page, limit]);

  React.useEffect(() => {
    if (deleteReport) {
      if (!customReportsArray.length) {
        setPage(page - 1);
      }
      setDeleteReport(false);
    }
  }, [deleteReport]);
  /******/

  const createReportBtn = (
    <Button
      text='Create New'
      icon={<FiPlus size={20} />}
      onClick={() => {
        if (isResellerAdmin) {
          setShowCustomerSelectorModal(true);
        } else if (isCompanyAdmin) {
          setSelectedUsers([]);
          setShowUserSelectorModal(true);
        } else {
          setShowCustomizeReportModal(true);
        }
      }}
    />
  );

  const customReportsArray = Object.keys(customReports).map(
    reportId => customReports[reportId]
  );

  const modalCustomTableProps = {};
  if (isResellerAdmin) {
    // @ts-ignore
    modalCustomTableProps.onBackClick = () => {
      setShowCustomizeReportModal(false);
      setShowCustomerSelectorModal(true);
    };
  } else if (isCompanyAdmin) {
    // @ts-ignore
    modalCustomTableProps.onBackClick = () => {
      setShowCustomizeReportModal(false);
      setShowUserSelectorModal(true);
    };
  }

  const monthlyReport =
    checkIfReportIsActive(timeRangeConstants.LAST_30_DAYS) || null;

  const weeklyReport =
    checkIfReportIsActive(timeRangeConstants.LAST_WEEK) || null;

  return (
    <>
      <Container>
        <MainWrapper resetPadding={false}>
          <HeaderWrapper>
            <Heading>Custom Reports</Heading>
            <Gap>
              {isCompanyAdmin && (
                <Button
                  variant='secondary'
                  text='Legacy Custom Reports'
                  onClick={() => {
                    window.location.href = `https://${whitelabel.domainV2}/backoffice/Email_Reports.php?r=4`;
                  }}
                />
              )}
              {createReportBtn}
            </Gap>
          </HeaderWrapper>
          <div style={{ marginTop: '32px', marginBottom: '10px' }}>
            {/*** SUS-781 changes ***/}
            {loading && <LoadingIndicator isLoading={loading} height='300px' />}
            {!loading && (
              <>
                {!!Number(userData?.reseller?.resellerReportingEnabled) &&
                  !!Number(userData.isResellerAdmin) && (
                    <RecurringReportContainer>
                      <RecurringReportItem
                        isActive={!!weeklyReport}
                        title={'Weekly'}
                        icon={<CalendarIcon color={theme.palette.blue100} />}
                        onClick={() => {
                          setRecurringReportData({
                            title: 'Weekly',
                            type: 'weekly',
                            reportData: {
                              frequency: weeklyReport?.frequency || '',
                              deliveryTime: weeklyReport?.deliveryTime || '',
                              resellerReportId:
                                weeklyReport?.resellerReportId || 0,
                            },
                          });
                          if (weeklyReport) {
                            formCustomerListFromReport(weeklyReport);
                          }
                          setIsSelectCustomerModalOpen(true);
                        }}
                        onDeactivate={() => {
                          setSelectedReport({
                            type: 'weekly',
                            reportId: weeklyReport?.resellerReportId || 0,
                          });
                          setIsDeactivateModalOpen(true);
                        }}
                      />
                      <RecurringReportItem
                        isActive={!!monthlyReport}
                        title={'Monthly'}
                        icon={
                          <CalendarMonthlyIcon
                            height={20}
                            color={theme.palette.blue100}
                          />
                        }
                        onClick={() => {
                          setRecurringReportData({
                            title: 'Monthly',
                            type: 'monthly',
                            reportData: {
                              frequency: monthlyReport?.frequency || '',
                              deliveryTime: monthlyReport?.deliveryTime || '',
                              resellerReportId:
                                monthlyReport?.resellerReportId || 0,
                            },
                          });
                          if (monthlyReport) {
                            formCustomerListFromReport(monthlyReport);
                          }
                          setIsSelectCustomerModalOpen(true);
                        }}
                        onDeactivate={() => {
                          setSelectedReport({
                            type: 'monthly',
                            reportId: monthlyReport?.resellerReportId || 0,
                          });
                          setIsDeactivateModalOpen(true);
                        }}
                      />
                    </RecurringReportContainer>
                  )}
                {!customReportsArray.length && (
                  <NoReports>
                    <div>No Custom Reports Created</div>
                    {createReportBtn}
                  </NoReports>
                )}

                {customReportsArray.map((report: any) => (
                  <CustomReportItem
                    report={report}
                    key={report.reportId}
                    setDeleteReport={setDeleteReport} /*** SUS-781 changes ***/
                  />
                ))}
              </>
            )}
          </div>
          {showCustomizeReportModal && (
            <ModalCustomTableFields
              title='New Custom Report'
              onClose={() => setShowCustomizeReportModal(false)}
              tableName={sectionName}
              onSave={fields => {
                setInitialReports(fields);
                setShowNewReportModal(true);
              }}
              allFields={allReports}
              initialFields={initialReports}
              btnText='Continue'
              saveToLocalStorage={false}
              {...modalCustomTableProps}
            />
          )}
          {showNewReportModal && (
            <CustomReportModal
              reports={initialReports}
              users={selectedUsers}
              handleModalClose={() => setShowNewReportModal(false)}
              onBackClick={() => {
                setShowNewReportModal(false);
                setShowCustomizeReportModal(true);
              }}
            />
          )}
          {showUserSelectorModal && (
            <UsersSelectorModal
              handleModalClose={() => setShowUserSelectorModal(false)}
              onSelect={userIds => {
                setShowUserSelectorModal(false);
                setSelectedUsers(userIds);
                setShowCustomizeReportModal(true);
              }}
              initUsers={selectedUsers}
            />
          )}
          {showCustomerSelectorModal && (
            <CustomersSelectorModal
              handleModalClose={() => setShowCustomerSelectorModal(false)}
              onSelect={userIds => {
                setShowCustomerSelectorModal(false);
                setSelectedUsers(userIds);
                setShowCustomizeReportModal(true);
              }}
              initUsers={selectedUsers}
            />
          )}
          {/*** SUS-781 changes ***/}
          {!loading && customReportsArray.length > 0 && (
            <Pagination
              limit={limit}
              page={page}
              setLimit={setLimit}
              setPage={setPage}
              totalCount={totalItems}
              currentCount={customReportsArray.length}
              lastPage={lastPage}
            />
          )}
          {isSelectCustomerModalOpen && (
            <SelectCustomersModal
              handleModalClose={() => setIsSelectCustomerModalOpen(false)}
              customersArray={allCustomers}
              setCustomersArray={setAllCustomers}
              title={`${recurringReportData.reportId ? `Edit` : ``} Recurring ${
                recurringReportData.title
              } Report`}
              onContinue={() => {
                formCustomerList(allCustomers);
                setIsSelectCustomerModalOpen(false);
                setIsCustomReportModalOpen(true);
              }}
              resetStates={resetStates}
            />
          )}
          {isCustomReportModalOpen && (
            <SimplifiedRecurringModal
              title={`${recurringReportData.reportId ? `Edit` : ``} Recurring ${
                recurringReportData.title
              } Report`}
              customers={customerList}
              handleModalClose={() => {
                setIsCustomReportModalOpen(false);
              }}
              resetStates={resetStates}
              report={recurringReportData}
              onBack={() => {
                setIsSelectCustomerModalOpen(true);
                setIsCustomReportModalOpen(false);
              }}
            />
          )}
          {isDeactivateModalOpen && (
            <DeactivateReportModal
              handleModalClose={() => {
                resetStates();
                setIsDeactivateModalOpen(false);
              }}
              type={selectedReport.type}
              onCancel={() => setIsDeactivateModalOpen(false)}
              onSubmit={() => {
                deactivateReport(selectedReport.reportId);
                resetStates();
                setIsDeactivateModalOpen(false);
              }}
            />
          )}
        </MainWrapper>
      </Container>
    </>
  );
};
